import type { SeverityTier } from '@/types'
import type { SliceMetrics } from '@/analysis/searchContract'
import { getRegionLabel } from '@/constants'

export type WorldSegment = 'overall' | 'delivery' | 'client'

type Tier = SeverityTier | null

/** Worst (lowest) non-null tier; null when every input is null. */
export function worstTier(...tiers: Tier[]): Tier {
  let best: Tier = null
  for (const t of tiers) {
    if (t == null) continue
    if (best == null || t < best) best = t
  }
  return best
}

type MetricsLike = Partial<SliceMetrics> | Record<string, { tier?: Tier } | null | undefined> | null | undefined

function tierOf(m: MetricsLike, key: string): Tier {
  if (!m || typeof m !== 'object') return null
  const entry = (m as Record<string, { tier?: Tier } | null | undefined>)[key]
  const t = entry?.tier
  return t === 0 || t === 1 || t === 2 ? t : null
}

/** Delivery / transit stress: demux_in_rate + download_kbps. */
export function deliverySevFromMetrics(metrics: MetricsLike): Tier {
  return worstTier(tierOf(metrics, 'demuxInRate'), tierOf(metrics, 'downloadKbps'))
}

/** Client-device stress: demux_to_decode_lag + render_ratio. */
export function clientSevFromMetrics(metrics: MetricsLike): Tier {
  return worstTier(tierOf(metrics, 'demuxToDecodeLag'), tierOf(metrics, 'renderRatio'))
}

type NodeLike = {
  avgSeverity?: Tier
  metrics?: MetricsLike
}

/** Segment severity for a search node; null → treat as neutral (never force red). */
export function segmentSevForNode(node: NodeLike, segment: WorldSegment): Tier {
  if (segment === 'delivery') return deliverySevFromMetrics(node.metrics)
  if (segment === 'client') return clientSevFromMetrics(node.metrics)
  const s = node.avgSeverity
  return s === 0 || s === 1 || s === 2 ? s : null
}

/** Split `EU|CGY` pair values; null when not pair-encoded. */
export function splitPairValue(value: string): { left: string; right: string } | null {
  const i = value.indexOf('|')
  if (i < 0) return null
  return { left: value.slice(0, i), right: value.slice(i + 1) }
}

/**
 * Infer a country code from synthetic cdn_edge labels like `Cloudflare-LON-GB`
 * or `ISP-GB` — last `-` token matching `/^[A-Z]{2}$/`.
 */
export function edgeCountryOf(cdnEdge: string): string | null {
  const parts = String(cdnEdge ?? '').split('-')
  for (let i = parts.length - 1; i >= 0; i--) {
    const tok = parts[i]!.trim().toUpperCase()
    if (/^[A-Z]{2}$/.test(tok)) return tok
  }
  return null
}

/** Human label for arc tooltips: `CGY → Europe`. */
export function flowArcLabel(pairValue: string, studioName?: string | null): string {
  const parts = splitPairValue(pairValue)
  if (!parts) return pairValue
  const studio = studioName || parts.right
  const continent = getRegionLabel(parts.left)
  return `${studio} → ${continent}`
}

/** Stroke/fill colors for tier (0=critical). Null → muted neutral. */
export function sevStroke(sev: Tier): string {
  if (sev === 0) return '#e84040'
  if (sev === 1) return '#f5a623'
  if (sev === 2) return '#34c95f'
  return 'rgba(180,190,210,0.55)'
}
